import React, { useEffect, useState } from 'react';
import { Link, useParams } from "react-router-dom";
import ReactModal from 'react-modal';
import DefaultLayout from '../layouts/DefaultLayout';
import Project from '../interfaces/Project';
import Audit from '../interfaces/Audit';
import Report from '../interfaces/Report';
import AdvisoriesSummaryNpm from '../components/AdvisoriesSummaryNpm';
import AdvisoriesSummaryComposer from '../components/AdvisoriesSummaryComposer';
import AdvisoriesSummaryRetire from '../components/AdvisoriesSummaryRetire';
import { fetchWithAuth } from '../services/Fetch';
import Pagination from '../interfaces/Pagination';
import JsonView from '@uiw/react-json-view';
import HeaderInspectionSummary from '../components/HeaderInspectionSummary';


const ProjectsDetail: React.FC = () => {

    const { id } = useParams<{ id: string }>();
    const [project, setProject] = useState<Project | null>(null);
    const [audits, setAudits] = useState<Audit[]>([]);
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [currentReport, setCurrentReport] = useState<object>({});
    const [total, setTotal] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [tool, setTool] = useState<string>("");
    const itemsPerPage = 10;

    const fetchProject = async () => {
        try {
            const response = await fetchWithAuth('/projects/' + id);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();

            setProject(data as Project);
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    };

    const fetchAudits = async () => {
        try {
            let endpoint = '/projects/' + id + '/audits?'
                + 'itemsPerPage=' + itemsPerPage
                + '&page=' + page;

            if (tool) {
                endpoint = endpoint + '&tool=' + tool;
            }
            const response = await fetchWithAuth(endpoint);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();

            setAudits(data.member.map((audit: any) => {
                const reportData = JSON.parse(audit.report.replace("\\r\\n", ""));

                let vulnerabilities = reportData.vulnerabilities || {};
                if (audit.tool === 'retire') {
                    vulnerabilities = [];
                    reportData.forEach((row: any) => {
                        if (row.results[0]) {
                            const issues = row.results[0].vulnerabilities || [];
                            issues.forEach((issue: any) => {
                                const version = [];
                                if (issue.atOrAbove) {
                                    version.push('>= ' + issue.atOrAbove);
                                }
                                if (issue.below) {
                                    version.push('< ' + issue.below);
                                }
                                const vulnerability = {
                                    title: issue.identifiers.summary,
                                    cves: issue.identifiers.CVE,
                                    severity: issue.severity,
                                    vulnerable_versions: version.join('; '),
                                    file: row.file,
                                    url_list: issue.info
                                };
                                vulnerabilities.push(vulnerability);
                            });
                        }
                    });
                }

                return {
                    id: audit.id,
                    // header-inspection reports are a flat header => message map
                    report: audit.tool === 'header-inspection' ? reportData : {
                        advisories: reportData.advisories || {},
                        abandonned: reportData.abandonned || {},
                        vulnerabilities: vulnerabilities || {},
                    } as Report,
                    tool: audit.tool,
                    auditAt: new Date(audit.auditAt)
                }
            }) as Audit[]);
            setTotal(data.totalItems);
        } catch (error) {
            console.error('Error fetching audits:', error);
        }
    };

    const openReport = (auditId: number) => {
        fetchWithAuth(`/projects/${id}/audits/${auditId}/report`)
            .then((response) => response.json())
            .then((json) => {
                setCurrentReport(json)
                setModalIsOpen(true);
            })
            .catch(error => console.error('Error downloading report:', error));
    }

    const onToolChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setTool(event.target.value);
        setPage(1);
    }

    const pagination = {} as Pagination;
    pagination.total = total;
    pagination.currentPage = page;
    pagination.itemsPerPage = itemsPerPage;
    pagination.firstPage = 1;
    pagination.lastPage = Math.ceil(total / itemsPerPage);

    useEffect(() => {
        fetchProject();
        fetchAudits();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        fetchAudits();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, tool]);

    if (!project) {
        return <DefaultLayout><h1>Chargement...</h1></DefaultLayout>;
    }

    return (
        <DefaultLayout>
            <h1>{project.customer && <>{project.customer?.title} - </>}{project.name}</h1>
            <p><a href={project.url} rel="noreferrer" target='_blank'>{project.url}</a></p>
            {project.customer && <><hr /><div style={{ whiteSpace: 'pre-line' }}>{project.customer?.description}</div></>}
            <form className="row g-3 mt-3">
                <div className="col-md-3">
                    <label htmlFor="filterTool" className="form-label">Filtrer par outil</label>
                    <select className="form-select" id="filterTool" value={tool} onChange={onToolChange}>
                        <option value="">Choisir une option</option>
                        <option value="npm">NPM</option>
                        <option value="composer">Composer</option>
                        <option value="retire">Retire</option>
                    </select>
                </div>
            </form>
            <table className="table table-striped mt-3">
                <thead>
                    <tr>
                        <th scope="col">Date</th>
                        <th scope="col">Outil</th>
                        <th scope="col">Rapport</th>
                    </tr>
                </thead>
                <tbody>
                    {audits && audits.length > 0 && audits.map(audit => <tr key={audit.id}>
                        <td>{audit.auditAt.toLocaleString()}</td>
                        <td>{audit.tool}</td>
                        <td>
                            {audit.tool === 'npm' && <AdvisoriesSummaryNpm report={audit.report} />}
                            {audit.tool === 'composer' && <AdvisoriesSummaryComposer report={audit.report} />}
                            {audit.tool === 'retire' && <AdvisoriesSummaryRetire report={audit.report} />}
                            {audit.tool === 'header-inspection' && <HeaderInspectionSummary report={audit.report} />}
                        </td>
                        <td>
                            <button type="button" onClick={() => openReport(audit.id)} className="btn btn-primary">
                                Voir le rapport complet
                            </button>
                        </td>
                    </tr>)}
                </tbody>
            </table>
            {pagination.lastPage > 1 && <>
                <nav aria-label="Page navigation" className="mt-4">
                    <ul className="pagination justify-content-center">
                        <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(pagination.currentPage - 1)}>
                                Précédent
                            </button>
                        </li>
                        {Array.from({ length: pagination.lastPage }, (_, index) => (
                            <li key={index} className={`page-item ${pagination.currentPage === index + 1 ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setPage(index + 1)}>
                                    {index + 1}
                                </button>
                            </li>
                        ))}
                        <li className={`page-item ${pagination.currentPage === pagination.lastPage ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(pagination.currentPage + 1)}>
                                Suivant
                            </button>
                        </li>
                    </ul>
                </nav>
            </>}
            <Link to="/projects" className="btn btn-success">Retour à la liste des projets</Link>
            <ReactModal
                isOpen={modalIsOpen}
                onRequestClose={() => setModalIsOpen(false)}>
                <JsonView value={currentReport} />
                <hr />
                <button className="btn btn-primary" onClick={() => setModalIsOpen(false)}>Fermer</button>
            </ReactModal>
        </DefaultLayout>
    );
}

export default ProjectsDetail;